<template>
    <!-- Filters -->
    <div class="mb-4 flex gap-4 flex-wrap">
        <div class="flex-1 min-w-[250px]">
            <IconField iconPosition="left">
                <InputIcon class="fa-solid fa-search"></InputIcon>
                <InputText :model-value="search" :placeholder="t('customFields.searchByTitle')" class="w-full"
                    @input="$emit('search', ($event.target as HTMLInputElement).value)" />
            </IconField>
        </div>
        <div class="min-w-[200px]">
            <Select :model-value="statusFilter" :options="statusOptions" optionLabel="label" optionValue="value"
                :placeholder="t('status.filterByStatus')" class="w-full"
                @change="$emit('statusChange', $event.value)" />
        </div>
        <Button icon="fa-solid fa-plus" :label="t('customFields.new')" @click="$emit('create')" />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card v-for="n in 6" :key="n">
            <template #content>
                <Skeleton height="150px" />
            </template>
        </Card>
    </div>

    <!-- Empty State -->
    <div v-else-if="customFields.length === 0" class="text-center py-12 text-gray-500">
        <i class="fa-regular fa-list text-3xl mb-3"></i>
        <p class="font-semibold">{{ t('customFields.noCustomFields') }}</p>
    </div>

    <!-- Cards Grid -->
    <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card v-for="customField in customFields" :key="customField.id" class="hover:shadow-lg transition-shadow">
                <template #header>
                    <div class="p-3 bg-primary-50 border-b">
                        <h3 class="text-2xl mb-1 font-bold text-primary-700">{{ customField.title }}</h3>
                    </div>
                </template>
                <template #content>
                    <div class="min-h-[60px]">
                        <p v-if="customField.description" class="text-gray-600">
                            {{ customField.description }}
                        </p>
                        <p v-else class="text-gray-400 italic">
                            {{ t('customFields.noDescription') }}
                        </p>
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-between items-center pt-1">
                        <Tag :value="customField.enabled ? t('common.enabled') : t('common.disabled')"
                            :severity="customField.enabled ? 'success' : 'danger'" class="px-3 py-1" />
                        <div class="flex gap-1">
                            <Button icon="fa-solid fa-edit" text rounded severity="primary" size="large"
                                @click="$emit('edit', customField)" />
                            <Button icon="fa-regular fa-trash-can" text rounded severity="danger" size="large"
                                @click="$emit('delete', customField)" />
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Paginator -->
        <Paginator :first="first" :rows="rows" :totalRecords="totalRecords" @page="$emit('page', $event)" class="mt-4"
            style="border: 1px solid var(--surface-border)" />
    </div>
</template>

<script setup lang="ts">
import type { CustomField } from '@/lib/types/customField';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    customFields: CustomField[];
    loading: boolean;
    search: string;
    statusFilter: boolean | null;
    first: number;
    rows: number;
    totalRecords: number;
}

defineProps<Props>();

defineEmits<{
    search: [value: string];
    statusChange: [value: boolean | null];
    page: [event: any];
    create: [];
    edit: [customField: CustomField];
    delete: [customField: CustomField];
}>();

const statusOptions = computed(() => [
    { label: t('status.all'), value: null },
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);
</script>
